/**
 * How a failure on our side looks on screen: the standard sentence, with the
 * support address as a mailto link, and no "Try again" (D31).
 *
 * - FailureText draws any failure sentence, turning the support address in
 *   it into a link, and a reference line at its end (E3, "Reference:
 *   d53e8822", from serverFailureText and serverFailureWords) as a small,
 *   selectable line in the muted theme colour. Screens that keep an error as a string (errorMessage's
 *   answer) draw it with this, so the standard sentence links wherever it
 *   lands; the toasts do too (toaster).
 * - ServerFailureNote is the sentence on its own, for a screen's error state
 *   in place of its own words and retry button. Given the error, it reports
 *   it to Sentry once (lib/serverFailure).
 * - ServerFailureScreen is the whole page when the account itself could not
 *   be loaded (Layout), with the way to sign out. When the server's request
 *   limit refused the read (limited, B24) it says the limit's calm sentence
 *   instead: a wait, not a fault, with the support address and no "try again".
 *
 * Who uses it: Layout, the toaster, and the screens' error states.
 * Words: serverFailure.message, serverFailure.title, serverFailure.limited,
 * serverFailure.limitedTitle, sitewide.logout.
 *
 * @module ServerFailure
 */

import React, { useMemo } from 'react';
import { cn } from '@/lib/utils';
import { SUPPORT_EMAIL } from '@/config/tunables/shared';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { reportServerFailure, referenceLine } from '@/shared/lib/serverFailure';
import { joinReference, splitReference } from '@/shared/lib/serverFailureCore';

/** A failure sentence, and the classes for its paragraph. */
interface FailureTextProps {
  /** The sentence; the support address in it becomes a mailto link. */
  text: string;
  /** Classes for the link, when the surrounding text needs a different underline colour. */
  linkClassName?: string;
}

/**
 * A failure sentence with the support address in it drawn as a mailto link.
 * Text without the address is drawn as it is.
 *
 * @returns The sentence, as inline content (no wrapping element of its own).
 */
export const FailureText: React.FC<FailureTextProps> = ({ text: whole, linkClassName }) => {
  const { sentence: text, reference } = splitReference(whole);
  const at = text.indexOf(SUPPORT_EMAIL);
  const line = reference ? <span className="mt-1 block select-text text-xs text-muted-foreground">{reference}</span> : null;
  if (at < 0) return <>{text}{line}</>;
  return (
    <>
      {text.slice(0, at)}
      <a
        href={`mailto:${SUPPORT_EMAIL}`}
        className={cn('underline underline-offset-2 break-words', linkClassName)}
      >
        {SUPPORT_EMAIL}
      </a>
      {text.slice(at + SUPPORT_EMAIL.length)}
      {line}
    </>
  );
};

/** The note's error and where it is drawn. */
interface ServerFailureNoteProps {
  /** The failure, to report once; omitted when the caller has reported it. */
  error?: unknown;
  /** The screen or step, for the report. */
  where?: string;
  /** Classes for the paragraph, in place of the default quiet red line. */
  className?: string;
  /** A reference code already known (the failure page's), when no error is passed. */
  code?: string;
}

/**
 * The standard sentence as a screen's error state: said once to Sentry, then
 * drawn with the support address as a link. No retry button, on purpose.
 *
 * @returns The paragraph.
 */
export const ServerFailureNote: React.FC<ServerFailureNoteProps> = ({ error, where, className, code }) => {
  const { reactTranslate: t } = useTranslation();
  // Reported once per error (reportServerFailure remembers), in the render so the reference code is known for the first paint.
  const ref = useMemo(() => (error === undefined ? undefined : (reportServerFailure(error, where), referenceLine(error))), [error, where]);
  const line = ref ?? (code ? t('serverFailure.reference', { code }) : undefined);
  return (
    <p role="alert" className={cn('text-sm text-destructive', className)}>
      <FailureText text={joinReference(t('serverFailure.message', { email: SUPPORT_EMAIL }), line)} />
    </p>
  );
};

/** The way out of the failure page, and why the account could not be read. */
interface ServerFailureScreenProps {
  /** The server's request limit refused the read (429 RATE_LIMITED): the limit's sentence, not the standard one. */
  limited?: boolean;
  /** The short reference code of the failure's Sentry report (E3), when there is one. */
  reference?: string;
  /** Signs out, for somebody signed in as the wrong person or wanting to start over. */
  onSignOut: () => void;
}

/**
 * The page shown when the account could not be loaded (a 500, no
 * connection, or the server's request limit on every try): never the
 * sign-up path, which would try to make a second account. The failure was
 * reported where it happened (AuthContext; the limit by the server too).
 *
 * @returns The page.
 */
export const ServerFailureScreen: React.FC<ServerFailureScreenProps> = ({ limited = false, reference, onSignOut }) => {
  const { reactTranslate: t } = useTranslation();
  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-background px-4 py-12 text-foreground">
      <div className="w-full max-w-md space-y-6 text-center">
        <p className="font-grow text-2xl font-bold leading-none text-primary">Grow</p>
        <h1 className="text-lg font-semibold">{t(limited ? 'serverFailure.limitedTitle' : 'serverFailure.title')}</h1>
        {limited ? (
          <p role="status" className="text-sm text-muted-foreground">
            <FailureText text={t('serverFailure.limited', { email: SUPPORT_EMAIL })} />
          </p>
        ) : (
          <ServerFailureNote code={reference} className="text-muted-foreground" />
        )}
        <button
          type="button"
          onClick={onSignOut}
          className="text-sm text-muted-foreground underline underline-offset-4"
        >
          {t('sitewide.logout')}
        </button>
      </div>
    </div>
  );
};
